import React from "react";
import { usePermissions } from "../../utils/hooks/usePermissions";
import { NoAccess } from "../NoAccess";

/**
 * PermissionGate - A component that conditionally renders children based on permissions
 *
 * Usage examples:
 * <PermissionGate permission="1_list_users">
 *   <Button>List Users</Button>
 * </PermissionGate>
 *
 * <PermissionGate permissions={["1_create_user", "1_update_user"]} mode="any">
 *   <UserForm />
 * </PermissionGate>
 *
 * <PermissionGate systemId={1} permission="list_users" resource={user}>
 *   <EditButton />
 * </PermissionGate>
 */
const PermissionGate = ({
  children,
  permission,
  permissions,
  systemId,
  mode = "any", // 'any' or 'all'
  resource = null,
  context = {},
  fallback = <NoAccess />,
  inverse = false, // If true, shows content when permission is NOT granted
}) => {
  const {
    hasAnyPermission,
    hasAllPermissions,
    hasPermissions
  } = usePermissions();

  let hasAccess = false;

  if (permissions && !!permissions.length && typeof permissions[0] !== "string") {
    hasAccess = hasPermissions(permissions, resource, context);
  } else if (permission) {
    // Check permission with full permission name
    hasAccess = hasAnyPermission([permission], resource, context);
  } else if (permissions && !!permissions.length) {
    // Check multiple permissions
    if (mode === "all") {
      hasAccess = hasAllPermissions(permissions, resource, context);
    } else {
      hasAccess = hasAnyPermission(permissions, resource, context);
    }
  }

// Apply inverse logic
if (inverse) {
  hasAccess = !hasAccess;
}

if (hasAccess) {
  return <>{children}</>;
}

return fallback ? <>{fallback}</> : null;
};

/**
 * Component wrapper for permission-based conditional rendering
 *
 * @param {string} permission - Single permission to check
 * @param {Array<string>} anyOf - Check if user has ANY of these permissions
 * @param {Array<string>} allOf - Check if user has ALL of these permissions
 * @param {React.ReactNode} fallback - Component to render when permission check fails
 * @param {React.ReactNode} children - Component to render when permission check passes
 *
 * @example
 * // Single permission
 * <Can permissions={[PERMISSIONS.SYSTEM1_CREATE_USER]}>
 *   <CreateButton />
 * </Can>
 *
 * @example
 * // Any of multiple permissions
 * <Can anyOf={[PERMISSIONS.SYSTEM1_CREATE_USER, PERMISSIONS.SYSTEM1_UPDATE_USER]}>
 *   <UserForm />
 * </Can>
 *
 * @example
 * // All of multiple permissions
 * <Can allOf={[PERMISSIONS.SYSTEM1_LIST_USERS, PERMISSIONS.SYSTEM1_CREATE_USER]}>
 *   <UserManagement />
 * </Can>
 *
 * @example
 * // With fallback
 * <Can permissions={[PERMISSIONS.VIEW_PLANNING]} fallback={<AccessDenied />}>
 *   <PlanningDashboard />
 * </Can>
 */
export function Can({
  permission,
  permissions,
  systemId,
  anyOf,
  allOf,
  fallback = null,
  children,
  resource = null,
  context = {},
  inverse = false

}) {
  const {
    hasAnyPermission,
    hasAllPermissions,
    hasPermissions
  } =
    usePermissions();

  let hasAccess = false;

  if (permissions && !!permissions.length) {
    hasAccess = hasAnyPermission(permissions, resource, context);
  } else if (permission) {
    hasAccess = hasAnyPermission([permission], resource, context);
  } else if (anyOf) {
    hasAccess = hasAnyPermission(anyOf, resource, context);
  } else if (allOf) {
    hasAccess = hasAllPermissions(allOf, resource, context);
  }

  return hasAccess ? children : fallback;
}

export default PermissionGate;

